// src/pages/MedicalCaseDiscussions/MedicalCaseDiscussions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
// ↑ Adaptez ce chemin d'import selon l'emplacement réel de votre LayoutDoctor.jsx

import { useMedicalCases } from "../../hooks/useMedicalCases";
import CreateCaseForm from "../../components/CreateCase/CreateCaseForm";
import ShareCaseChoice from "../../components/ShareCaseChoice/ShareCaseChoice";
import GoLiveStreamForm from "../../components/GoLiveStreamForm/GoLiveStreamForm";
import SpecialtyFilter from "../../components/Specialtyfilter/Specialtyfilter";
import CaseFeed from "../../components/Casefeed/Casefeed";
import GoLiveCard from "../../components/Golivecard/Golivecard";
import OnlineNowCard from "../../components/Onlinenowcard/Onlinenowcard";
import { CASE_DISCUSSIONS_ROUTES } from "../../routePaths";
import { Avatar, Spin } from "antd";
import useDoctorInformation from "../../../ProfileDoctor/hooks/useDoctorInformation";
import useSavedItems from "../../../authorization/useSavedItems";

import "./Medicalcasediscussions.css";

const LIVE_CARD_REFRESH_INTERVAL_MS = 3000;

/* ─── Données de démo (à remplacer par un appel API / Redux) ───
   IMPORTANT : ces valeurs restent en anglais car ce sont des VALEURS
   (envoyées à l'API et utilisées pour filtrer). Pour AFFICHER un libellé
   traduit, utilisez getSpecialtyLabel(t, value) (voir ./specialtyLabel.js). */
const SPECIALTIES = [
  "All",
  "General Medicine / Family Medicine",
  "Internal Medicine",
  "Geriatrics",
  "Emergency Medicine",
  "Occupational Medicine",
  "Preventive Medicine",
  "Physical and Rehabilitation Medicine",
  "Sports Medicine",
  "Forensic Medicine",
  "Nuclear Medicine",
  "Palliative Medicine",
  "Tropical Medicine",
  "Cardiology",
  "Interventional Cardiology",
  "Cardiac Surgery",
  "Vascular Surgery",
  "Angiology",
  "Neurology",
  "Neurosurgery",
  "Neuroradiology",
  "Psychiatry",
  "Neuropsychiatry",
  "Pulmonology",
  "Thoracic Surgery",
  "Allergology",
  "Rheumatology",
  "Orthopedics",
  "Traumatology",
  "Orthopedic Surgery",
  "Ophthalmology",
  "Ophthalmic Surgery",
  "Otorhinolaryngology (ENT)",
  "Head and Neck Surgery",
  "Audiology",
  "Dentistry / Odontology",
  "Orthodontics",
  "Periodontology",
  "Endodontics",
  "Oral and Maxillofacial Surgery",
  "Prosthodontics",
  "Hematology",
  "Clinical Hematology",
  "Blood Transfusion",
  "Onco-Hematology",
  "Medical Oncology",
  "Radiation Oncology",
  "Surgical Oncology",
  "Anatomical Pathology",
  "Endocrinology",
  "Diabetology",
  "Nutrition",
  "Metabolic Diseases",
  "Gastroenterology",
  "Hepatology",
  "Digestive Surgery",
  "Proctology",
  "Nephrology",
  "Urology",
  "Urological Surgery",
  "Andrology",
  "Gynecology",
  "Obstetrics",
  "Obstetrics and Gynecology",
  "Reproductive Medicine",
  "Fertility / Assisted Reproduction",
  "Gynecologic Oncology",
  "Pediatrics",
  "Neonatology",
  "Specialized Pediatrics",
  "Pediatric Surgery",
  "Pediatric Cardiology",
  "Pediatric Neurology",
  "Dermatology",
  "Aesthetic Dermatology",
  "Dermatovenereology",
  "Dermatologic Surgery",
  "Infectious Diseases",
  "Infectiology",
  "Medical Microbiology",
  "Parasitology",
  "Medical Biology",
  "Medical Biochemistry",
  "Biological Hematology",
  "Immunology",
  "Microbiology",
  "Medical Genetics",
  "Cytogenetics",
  "Radiology",
  "Interventional Radiology",
  "Ultrasound",
  "Cardiovascular Imaging",
  "Musculoskeletal Imaging",
  "General Surgery",
  "Cardiovascular Surgery",
  "Plastic, Reconstructive and Aesthetic Surgery",
  "Maxillofacial Surgery",
  "Gynecologic Surgery",
  "Transplant Surgery",
  "Child Psychiatry",
  "Geriatric Psychiatry",
  "Addiction Medicine",
  "Liaison Psychiatry",
  "Clinical Immunology",
  "Rare Disease Medicine",
  "Anesthesiology",
  "Critical Care / Intensive Care",
  "Intensive Medicine",
  "Anesthesiology and Intensive Care",
];

export default function MedicalCaseDiscussions() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSpecialty, setActiveSpecialty] = useState("All");
  const [showCreate, setShowCreate] = useState(false);
  const [showShareChoice, setShowShareChoice] = useState(false);
  const [showGoLiveForm, setShowGoLiveForm] = useState(false);
  const [optimisticCases, setOptimisticCases] = useState([]);
  const [createCaseError, setCreateCaseError] = useState(null);

  useEffect(() => {
    if (!location.state?.openCreate && !location.state?.openGoLive) return;

    setShowShareChoice(false);
    setShowCreate(Boolean(location.state.openCreate));
    setShowGoLiveForm(Boolean(location.state.openGoLive));
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const currentDoctor = useSelector((state) => state?.doctor?.user || null);
  const normalizeDoctorId = (id) => String(id ?? "").trim().toLowerCase();
  const currentDoctorIds = useMemo(
    () => new Set([
      currentDoctor?.id,
      currentDoctor?._id,
      currentDoctor?.doctorId,
      currentDoctor?.medecinId,
      currentDoctor?.userId,
      currentDoctor?.pgId,
      currentDoctor?.profileId,
      currentDoctor?.raw?.id,
      currentDoctor?.raw?._id,
      currentDoctor?.raw?.doctorId,
      currentDoctor?.raw?.userId,
    ].map(normalizeDoctorId).filter(Boolean)),
    [currentDoctor]
  );
  const { isSaved, toggleSave, pendingKey } = useSavedItems({ enabled: !!currentDoctor });
  const { avatarSrc } = useDoctorInformation(currentDoctor || {});
  const createCardInitials = useMemo(() => {
    // Ce texte n'est jamais affiché : il sert uniquement à calculer les initiales.
    const baseName = currentDoctor?.fullName || currentDoctor?.name || currentDoctor?.displayName || "Dr. Unknown";
    return baseName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part?.[0]?.toUpperCase() || "")
      .join("") || "DR";
  }, [currentDoctor]);

  const {
    fetchPublicCases,
    fetchMyCases,
    fetchMyLives,
    fetchPublicLives,
    publicCases = [],
    myCases = [],
    myLives = [],
    publicLives = [],
    publicStatus,
    myStatus,
    createCase,
    updateCase,
    deleteCase,
    createLive,
    endLive,
    cancelLive,
    addFiles,
    error,
  } = useMedicalCases();

  useEffect(() => {
    fetchPublicCases({ page: 1, limit: 20 });
    fetchMyCases({ page: 1, limit: 20 });
    fetchMyLives({ page: 1, limit: 20 });
    fetchPublicLives({ page: 1, limit: 20 });
  }, [fetchPublicCases, fetchMyCases, fetchMyLives, fetchPublicLives]);

  const hasActiveLive = useMemo(
    () =>
      [...(Array.isArray(myLives) ? myLives : []), ...(Array.isArray(publicLives) ? publicLives : [])]
        .some((item) => String(item?.status || "").toLowerCase() === "live"),
    [myLives, publicLives]
  );

  useEffect(() => {
    if (!hasActiveLive) return undefined;

    let refreshing = false;
    const refreshLives = async () => {
      if (refreshing || document.visibilityState === "hidden") return;
      refreshing = true;
      try {
        await Promise.allSettled([
          fetchMyLives({ page: 1, limit: 20 }),
          fetchPublicLives({ page: 1, limit: 20 }),
        ]);
      } finally {
        refreshing = false;
      }
    };

    const timer = window.setInterval(refreshLives, LIVE_CARD_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshLives);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshLives);
    };
  }, [fetchMyLives, fetchPublicLives, hasActiveLive]);

  const apiCases = useMemo(() => {
    const unknownDoctor = t("medicalCases.unknownDoctor");
    // Dates formatées selon la langue active (fr, ar, en)
    const formatDate = (value) => new Date(value).toLocaleDateString(i18n.language);

    const toViewModel = (source, indexOffset = 0, canManage = false) => {
      const items = Array.isArray(source) ? source : source?.items || [];
      return items.map((item, index) => {
        const fullName = item?.author?.fullName || item?.doctor?.fullName || item?.author?.name || item?.doctor?.name || unknownDoctor;
        const initials = fullName
          .split(" ")
          .map((part) => part?.[0]?.toUpperCase() || "")
          .filter(Boolean)
          .slice(0, 2)
          .join("") || "DR";
        // Valeur brute (anglais) conservée : c'est elle qui sert au filtre.
        const specialty = item?.medicalSpeciality || item?.medicalSpecialty || item?.specialty || "General";
        const isLive =
          String(item?.status || item?.liveStatus || "").toLowerCase() === "live";

        return {
          id: item?.id || item?._id || `case-${indexOffset + index}`,
          canManage: canManage || [
            item?.doctorId,
            item?.authorId,
            item?.userId,
            item?.doctor?.id,
            item?.doctor?._id,
            item?.doctor?.doctorId,
            item?.author?.id,
            item?.author?._id,
            item?.author?.doctorId,
            item?.createdBy?.id,
            item?.createdBy?._id,
          ].some((id) => currentDoctorIds.has(normalizeDoctorId(id))),
          specialty,
          title: item?.caseTitle || item?.title || t("medicalCases.untitledCase"),
          description: item?.caseDescription || item?.description || "",
          author: {
            name: fullName,
            initials,
            color: item?.author?.color || ["#c7d2fe", "#bbf7d0", "#fecdd3", "#fde68a"][indexOffset + index % 4],
          },
          time: item?.createdAt ? formatDate(item.createdAt) : t("medicalCases.recentlyAdded"),
          commentsCount: item?.commentsCount ?? item?.commentCount ?? 0,
          likesCount: item?.likesCount ?? item?.likeCount ?? 0,
          likedByCurrentDoctor: Boolean(item?.likedByCurrentDoctor ?? item?.liked),
          isLive,
          watchingCount: item?.watchingCount || 0,
          raw: item,
        };
      });
    };

    const livesById = new Map();
    [...(Array.isArray(myLives) ? myLives : []), ...(Array.isArray(publicLives) ? publicLives : [])]
      .forEach((item) => {
        if (String(item?.status || "").toLowerCase() !== "live") return;
        if (item?.id && !livesById.has(item.id)) livesById.set(item.id, item);
      });
    const liveCases = Array.from(livesById.values()).map((item) => {
      const liveHostDoctorId =
        item?.hostDoctorId ||
        item?.host_doctor_id ||
        item?.hostDoctor?.id ||
        item?.hostDoctor?._id ||
        item?.doctorId;
      const fullName =
        item?.hostDoctor?.fullName ||
        item?.author?.fullName ||
        item?.doctor?.fullName ||
        unknownDoctor;
      return {
        id: item.id,
        specialty: "General",
        title: item.title || t("medicalCases.liveCase"),
        description: item.description || "",
        author: { name: fullName, initials: fullName.split(/\s+/).map((part) => part[0]?.toUpperCase() || "").join("").slice(0, 2) || "DR" },
        time: item?.startedAt || item?.createdAt ? formatDate(item.startedAt || item.createdAt) : t("medicalCases.recentlyStarted"),
        commentsCount: 0,
        isLive: item.status === "live",
        canManageLive: currentDoctorIds.has(normalizeDoctorId(liveHostDoctorId)),
        watchingCount: Number(item?.watchingCount) || 0,
        raw: item,
      };
    });

    const merged = [
      ...liveCases,
      ...optimisticCases,
      ...toViewModel(myCases, 0, true),
      ...toViewModel(publicCases, myCases.length),
    ];
    const activeLiveIds = new Set(livesById.keys());
    const liveOnlyCases = merged.filter((caseItem) => {
      const rawItem = caseItem?.raw || {};
      const hasLiveMarker = Boolean(
        rawItem?.liveId ||
        rawItem?.live ||
        rawItem?.isLive ||
        rawItem?.isStreaming ||
        rawItem?.streaming ||
        rawItem?.status === "live" ||
        rawItem?.liveStatus
      );
      if (!hasLiveMarker) return true;

      const liveId = rawItem?.liveId || rawItem?.id || caseItem?.id;
      return activeLiveIds.has(liveId);
    });
    const uniqueById = new Map();

    liveOnlyCases.forEach((caseItem) => {
      if (!uniqueById.has(caseItem.id)) {
        uniqueById.set(caseItem.id, caseItem);
      }
    });

    return Array.from(uniqueById.values());
    // `t` et la langue active sont des dépendances : la liste se recalcule
    // (titres par défaut, dates) quand l'utilisateur change de langue.
  }, [myCases, myLives, optimisticCases, publicCases, publicLives, currentDoctorIds, t, i18n.language]);

  const specialtyOptions = useMemo(() => {
    const values = apiCases.map((caseItem) => caseItem.specialty).filter(Boolean);
    return ["All", ...Array.from(new Set([...SPECIALTIES.slice(1), ...values]))];
  }, [apiCases]);

  const filteredCases = useMemo(() => {
    if (activeSpecialty === "All") return apiCases;
    return apiCases.filter((c) => c.specialty === activeSpecialty);
  }, [activeSpecialty, apiCases]);

  const handleCaseClick = (caseItem) => {
    const caseId =
      caseItem?.raw?.id ||
      caseItem?.raw?._id ||
      caseItem?.id ||
      caseItem?._id;

    if (!caseId) return;

    navigate(CASE_DISCUSSIONS_ROUTES.DETAIL(caseId), {
      state: { caseItem },
    });
  };

  const handleEditCase = async (caseId, updates) => {
    await updateCase(caseId, updates).unwrap();
    await Promise.all([
      fetchMyCases({ page: 1, limit: 20 }),
      fetchPublicCases({ page: 1, limit: 20 }),
    ]);
  };

  const handleDeleteCase = async (caseId) => {
    await deleteCase(caseId).unwrap();
    await Promise.all([
      fetchMyCases({ page: 1, limit: 20 }),
      fetchPublicCases({ page: 1, limit: 20 }),
    ]);
  };

  const handleDeleteLive = async (liveId, liveStatus) => {
    if (!liveId) return;
    const isActiveLive = String(liveStatus || "").toLowerCase() === "live";
    const action = isActiveLive ? endLive : cancelLive;
    await action(liveId).unwrap();
    await Promise.allSettled([
      fetchMyLives({ page: 1, limit: 20 }),
      fetchPublicLives({ page: 1, limit: 20 }),
    ]);
  };

  const handleStartLiveStream = () => {
    setShowCreate(false);
    setShowGoLiveForm(false);
    setShowShareChoice(true);
  };

  const handleBackFromShareChoice = () => {
    setShowShareChoice(false);
  };

  const handleCreateStandardCaseFromShareChoice = () => {
    setShowShareChoice(false);
    setShowCreate(true);
  };

  const handleGoLiveFromShareChoice = () => {
    setShowShareChoice(false);
    setShowCreate(false);
    setShowGoLiveForm(true);
  };

  const handleBackFromGoLiveForm = () => {
    setShowGoLiveForm(false);
    setShowShareChoice(true);
  };

  const handleCancelGoLiveForm = () => {
    setShowGoLiveForm(false);
    setShowShareChoice(false);
  };

  const handleSubmitGoLiveForm = async (liveData) => {
    try {
      const livePayload = await createLive({
        title: liveData.title,
        description: liveData.description,
        privacy: liveData.privacy,
        maxParticipants: liveData.maxParticipants,
      }).unwrap();

      const liveId = livePayload?.id || livePayload?._id || livePayload?.liveId;
      if (!liveId) {
        throw new Error("Live creation returned no valid ID");
      }

      const streamData = {
        id: liveId,
        ...livePayload,
        roomName:
          livePayload?.roomName ||
          livePayload?.room_name ||
          `medical-live-${liveId}`,
        specialty:
          liveData?.specialty ||
          livePayload?.medicalSpeciality ||
          livePayload?.medicalSpecialty ||
          livePayload?.specialty ||
          "General",
        privacy: livePayload?.privacySetting || livePayload?.privacy,
      };

      try {
        sessionStorage.setItem(`medilink_live_stream_${liveId}`, JSON.stringify(streamData));
      } catch {
        // Ignore storage write errors and continue navigation.
      }

      setShowGoLiveForm(false);
      setShowShareChoice(false);
      setShowCreate(false);
      navigate(CASE_DISCUSSIONS_ROUTES.LIVE(liveId), {
        state: { streamData },
      });
    } catch (err) {
      console.error("Failed to create live stream", err);
      // Optionally add UI error handling here.
    }
  };

  const handleCreateCase = async (formData) => {
    const payload = {
      caseTitle: formData.title,
      medicalSpeciality: formData.specialty,
      caseDescription: formData.description,
      privacySetting: formData.privacy,
    };

    setCreateCaseError(null);

    try {
      // IMPORTANT: `.unwrap()` is required here. `createCase()` just returns
      // the raw `dispatch(...)` promise, which — because the thunks use
      // `rejectWithValue` — always *resolves* even when the API call fails.
      // Without `.unwrap()` this try/catch would never see a failure, and
      // an optimistic card would be added to the feed even though the case
      // was never actually created on the server.
      const createdCase = await createCase(payload).unwrap();
      const caseId = createdCase?.id || createdCase?._id;

      const doctorName =
        currentDoctor?.fullName ||
        currentDoctor?.name ||
        currentDoctor?.displayName ||
        t("medicalCases.unknownDoctor");

      const optimisticCase = {
        id: caseId || `optimistic-${Date.now()}`,
        specialty: formData.specialty || "General",
        title: formData.title || t("medicalCases.untitledCase"),
        description: formData.description || "",
        author: {
          name: doctorName,
          initials: (currentDoctor?.fullName || currentDoctor?.name || currentDoctor?.displayName || "DR")
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part?.[0]?.toUpperCase() || "")
            .join("") || "DR",
          color: "#c7d2fe",
        },
        time: t("medicalCases.justNow"),
        commentsCount: 0,
        isLive: false,
        watchingCount: 0,
        raw: {
          id: caseId,
          caseTitle: formData.title,
          caseDescription: formData.description,
          medicalSpeciality: formData.specialty,
          createdAt: new Date().toISOString(),
          author: currentDoctor,
        },
      };

      setOptimisticCases((prev) => [optimisticCase, ...prev]);

      if (caseId && formData.attachments?.length) {
        try {
          await addFiles(caseId, formData.attachments).unwrap();
        } catch (attachErr) {
          // The case itself was created successfully; only the attachment
          // upload failed. Surface it but don't roll back the optimistic
          // card or block the flow.
          console.error("Attachment upload failed", attachErr);
        }
      }

      // fetchPublicCases/fetchMyCases now return the dispatch promise, so
      // these awaits genuinely wait for the refreshed lists instead of
      // resolving instantly.
      await fetchPublicCases({ page: 1, limit: 20 });
      await fetchMyCases({ page: 1, limit: 20 });
      setShowCreate(false);
    } catch (err) {
      console.error("Create case failed", err);
      setCreateCaseError(
        typeof err === "string" ? err : err?.message || t("medicalCases.createCaseFailed")
      );
    }
  };

  const isLoading = publicStatus === "loading" || myStatus === "loading";

  return (
    <AppLayoutDoctor>
      <div className="mcd-page">
        <div className={`mcd-page__body ${showShareChoice || showGoLiveForm ? "mcd-page__body--share" : ""}`}>
          <div className="mcd-page__main">
            {showCreate ? (
              <CreateCaseForm
                open={true}
                isPage={true}
                onClose={() => setShowCreate(false)}
                onCreate={handleCreateCase}
                specialties={specialtyOptions.filter((item) => item !== "All")}
                submitError={createCaseError}
              />
            ) : showGoLiveForm ? (
              <GoLiveStreamForm
                specialties={specialtyOptions.filter((item) => item !== "All")}
                onBack={handleBackFromGoLiveForm}
                onCancel={handleCancelGoLiveForm}
                onSubmit={handleSubmitGoLiveForm}
              />
            ) : showShareChoice ? (
              <ShareCaseChoice
                onBack={handleBackFromShareChoice}
                onStandardCase={handleCreateStandardCaseFromShareChoice}
                onGoLive={handleGoLiveFromShareChoice}
              />
            ) : (
              <>
                <div className="mcd-page__main-header">
                  <div className="mcd-create-card">
                    <button className="mcd-create-story-card" onClick={() => setShowCreate(true)} aria-label={t("createCase.createStory")}>
                      <span className="mcd-create-story-avatar">
                        <Avatar
                          size={42}
                          src={avatarSrc || undefined}
                          style={{ backgroundColor: "#b1cefc", color: "#fff" }}
                        >
                          {createCardInitials}
                        </Avatar>
                        <span className="mcd-create-story-avatar-plus">+</span>
                      </span>
                      <span className="mcd-create-story-label">{t("createCase.createStory")}</span>
                    </button>
                  </div>
                  <SpecialtyFilter
                    specialties={specialtyOptions}
                    activeSpecialty={activeSpecialty}
                    onChange={setActiveSpecialty}
                  />
                </div>

                {error ? <div className="mcd-page__error">{error}</div> : null}

                {isLoading ? (
                  <div className="mcd-page__loading">
                    <Spin size="large" />
                  </div>
                ) : (
                  <CaseFeed
                    cases={filteredCases}
                    onCaseClick={handleCaseClick}
                    isSaved={isSaved}
                    onToggleSave={(type, id) => toggleSave(type, id)}
                    pendingKey={pendingKey}
                    onEditCase={handleEditCase}
                    onDeleteCase={handleDeleteCase}
                    onDeleteLive={handleDeleteLive}
                  />
                )}
              </>
            )}
          </div>

          {!showShareChoice && !showGoLiveForm ? (
            <aside className="mcd-page__aside">
              <GoLiveCard onStartLiveStream={handleStartLiveStream} />
            </aside>
          ) : null}
        </div>
      </div>
    </AppLayoutDoctor>
  );
}